<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.1 / WEB.2 — the app-root call layer. Mounted once in
// App.vue so the toast and the softphone survive every route change. It
// wires the call store to ActionCable (via the bus), alerts, the ringtone,
// browser notifications, rumo-desktop's preload bridge (DESKTOP.*), and — in
// dev builds only — the mock layer.
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { useAlert } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import KaptionIncomingCallToast from './KaptionIncomingCallToast.vue';
import KaptionIncomingPassToast from './KaptionIncomingPassToast.vue';
import KaptionCallDock from './KaptionCallDock.vue';
import KaptionPassLineDialog from './KaptionPassLineDialog.vue';
import { useKaptionCallStore } from './kaptionCallStore';
import { subscribeKaptionCallEvents } from './kaptionCallEvents';
import { startRingtone, stopRingtone } from './kaptionRingtone';
import {
  buildDesktopState,
  getDesktopCallsBridge,
  getDesktopMediaPreferences,
  getDesktopRingSinkId,
  handleDesktopAction,
} from './kaptionDesktopCallsBridge';

const { t } = useI18n();
const router = useRouter();
const vuexStore = useStore();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const calls = useKaptionCallStore();

const isEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_WHATSAPP_CALLING')
);
const isRecordingEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_CALL_RECORDING')
);

const currentUser = computed(() => vuexStore.getters.getCurrentUser);
const accountId = computed(() => vuexStore.getters.getCurrentAccountId);
const busyId = ref(null);

calls.configure({
  getCurrentUserId: () => currentUser.value?.id,
  getMediaPreferences: getDesktopMediaPreferences,
  notify: code => useAlert(t(`KAPTION_CALLS.ERRORS.${code}`)),
});

// ROUTING.5 / API.6 — the membership's call availability, read from
// GET /kaption/call_availability whenever calling is on for the account
// (the account payload carries it only if a future serializer adds it).
watch(
  [isEnabled, accountId],
  ([enabled]) => {
    if (!enabled) return;
    calls.loadAvailability();
    // whatsapp-calling-ownership.LINE.3 — lines for the status menu + tray.
    calls.loadLines();
  },
  { immediate: true }
);
watch(
  [currentUser, accountId],
  () => {
    const membership = (currentUser.value?.accounts || []).find(
      account => account.id === accountId.value
    );
    calls.hydrateAvailability(membership?.kaption_call_availability);
  },
  { immediate: true }
);

const ringingCalls = calls.ringingCalls;
const activeCall = calls.activeCall;
const heldCall = calls.heldCall;
const showToast = computed(
  () => isEnabled.value && ringingCalls.value.length > 0
);
const showDock = computed(() => isEnabled.value && !!activeCall.value);
// whatsapp-calling-ownership.RING.5 — ringing here + waiting elsewhere.
const waitingCount = computed(() => calls.waitingCount.value);

// whatsapp-calling-ownership.UI.3 / UI.4 — passing a live call.
const incomingPasses = calls.incomingPasses;
const showPassToast = computed(
  () => isEnabled.value && incomingPasses.value.length > 0
);
const passTargets = ref([]);
const passTargetsLoading = ref(false);
const passBusy = ref(false);
const isAdmin = computed(() => currentUser.value?.role === 'administrator');
// PASS.1 — only the connected agent (or an admin) can pass.
const canPass = computed(
  () =>
    !!activeCall.value &&
    (isAdmin.value ||
      !activeCall.value.answeredBy?.id ||
      activeCall.value.answeredBy.id === currentUser.value?.id)
);
// whatsapp-call-transcripts.REC.UI — the decision was made at call creation
// (call.recording); a call without it is not recorded.
const callRecording = computed(() =>
  isRecordingEnabled.value ? activeCall.value?.recording || null : null
);
const noticeBusy = ref(false);
const playNotice = async () => {
  noticeBusy.value = true;
  try {
    await calls.playRecordingNotice();
  } finally {
    noticeBusy.value = false;
  }
};
// CONSENT.4 — the connected agent stops the recording.
const canStopRecording = computed(
  () =>
    !!activeCall.value?.answeredBy?.id &&
    activeCall.value.answeredBy.id === currentUser.value?.id
);
const receivedPass = computed(() => {
  const pass = activeCall.value?.pass;
  return pass && pass.toUser?.id === currentUser.value?.id ? pass : null;
});
const mutedAfterPass = computed(
  () =>
    !!calls.state.unmutePassId &&
    calls.state.passes[calls.state.unmutePassId]?.callId ===
      activeCall.value?.id
);

const loadPassTargets = async () => {
  passTargetsLoading.value = true;
  passTargets.value = await calls.loadPassTargets();
  passTargetsLoading.value = false;
};
const withPassBusy = async action => {
  passBusy.value = true;
  try {
    await action();
  } finally {
    passBusy.value = false;
  }
};
const startPass = payload => withPassBusy(() => calls.startPass(payload));
const completePass = () => withPassBusy(() => calls.completePass());
// whatsapp-calling-ai.UI.3 — "Take the call" from the AI's briefing.
const takeCall = () => withPassBusy(() => calls.takeCall(receivedPass.value));
const cancelPass = () => withPassBusy(() => calls.cancelPass());
const leavePass = () =>
  receivedPass.value && calls.declinePass(receivedPass.value.id);
const answerPass = async passId => {
  busyId.value = passId;
  await calls.acceptPass(passId);
  busyId.value = null;
};
const declinePass = passId => calls.declinePass(passId);

const answer = async id => {
  busyId.value = id;
  await calls.accept(id);
  busyId.value = null;
};
const decline = id => calls.decline(id);

const openConversation = (call = activeCall.value) => {
  if (!call?.conversationId) return;
  router.push(
    frontendURL(
      conversationUrl({ accountId: accountId.value, id: call.conversationId })
    )
  );
};

// Ringtone + browser notification (the desktop app shows its own native one).
let browserNotification = null;
const notifyInBrowser = (call, pass = null) => {
  if (getDesktopCallsBridge()) return;
  if (!('Notification' in window) || Notification.permission !== 'granted')
    return;
  if (!document.hidden) return;
  try {
    const body = t('KAPTION_CALLS.NOTIFICATION.BODY', {
      name: call.contactName,
      inbox: call.inboxName,
    });
    browserNotification = new Notification(
      pass
        ? t('KAPTION_CALLS.PASS.INCOMING', { name: pass.fromUser?.name || '' })
        : t('KAPTION_CALLS.NOTIFICATION.TITLE'),
      {
        body: pass?.note ? `${body}\n“${pass.note}”` : body,
        tag: `kaption-call-${call.id}`,
        requireInteraction: true,
      }
    );
    browserNotification.onclick = () => window.focus();
  } catch (e) {
    browserNotification = null;
  }
};

const ringingKey = computed(() =>
  [
    ...ringingCalls.value.map(c => c.id),
    ...incomingPasses.value.map(p => `p${p.id}`),
  ].join(',')
);

watch(
  () => [showToast.value || showPassToast.value, ringingKey.value],
  ([ringing], previous) => {
    if (!ringing) {
      stopRingtone();
      browserNotification?.close();
      browserNotification = null;
      return;
    }
    const wasRingingIds = (previous?.[1] || '').split(',');
    const waiting = !!activeCall.value;
    getDesktopRingSinkId().then(sinkId => {
      if (showToast.value || showPassToast.value)
        startRingtone({ waiting, sinkId });
    });
    const newest = ringingCalls.value[ringingCalls.value.length - 1];
    if (newest && !wasRingingIds.includes(String(newest.id)))
      notifyInBrowser(newest);
    const newestPass = incomingPasses.value[incomingPasses.value.length - 1];
    if (newestPass && !wasRingingIds.includes(`p${newestPass.id}`))
      notifyInBrowser(newestPass.call, newestPass);
  }
);

// DESKTOP.1..5 — publish state to rumo-desktop and take its actions.
const desktopBridge = getDesktopCallsBridge();
let unsubscribeDesktop = null;
if (desktopBridge) {
  watch(
    () =>
      buildDesktopState({
        ringingCalls: isEnabled.value ? ringingCalls.value : [],
        incomingPasses: isEnabled.value ? incomingPasses.value : [],
        heldLines: isEnabled.value ? calls.heldLines.value : [],
        activeCall: activeCall.value,
        heldCall: heldCall.value,
        muted: calls.state.muted,
        onHold: calls.state.onHold,
        availability: calls.state.availability,
      }),
    state => desktopBridge.publishState?.(JSON.parse(JSON.stringify(state))),
    { deep: true, immediate: true }
  );
}

const warnBeforeUnload = event => {
  if (!activeCall.value) return;
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};

let unsubscribeEvents = null;
onMounted(() => {
  unsubscribeEvents = subscribeKaptionCallEvents(calls, emitter);
  if (desktopBridge?.onAction) {
    unsubscribeDesktop = desktopBridge.onAction(action => {
      if (action?.type === 'focus_call') {
        openConversation(
          activeCall.value ||
            ringingCalls.value[0] ||
            incomingPasses.value[0]?.call
        );
        return;
      }
      handleDesktopAction(calls, action);
    });
  }
  window.addEventListener('beforeunload', warnBeforeUnload);
  // Dev-only mock layer; `import.meta.env.DEV` is false in production
  // builds, so the mock module is never bundled there.
  if (import.meta.env.DEV) {
    import('./mock/kaptionCallMock').then(mock => {
      if (!mock.isKaptionCallMockRequested()) return;
      mock
        .installKaptionCallMock({
          callStore: calls,
          vuexStore,
          emitter,
          getCurrentUser: () => currentUser.value,
        })
        .enableFlags();
    });
  }
});

onBeforeUnmount(() => {
  unsubscribeEvents?.();
  unsubscribeDesktop?.();
  stopRingtone();
  window.removeEventListener('beforeunload', warnBeforeUnload);
});
</script>

<template>
  <div v-if="isEnabled" class="contents">
    <KaptionIncomingPassToast
      v-if="showPassToast"
      :passes="incomingPasses"
      :busy-id="busyId"
      @answer="answerPass"
      @decline="declinePass"
    />
    <KaptionIncomingCallToast
      v-if="showToast"
      :stacked="showPassToast"
      :calls="ringingCalls"
      :on-call="!!activeCall"
      :busy-id="busyId"
      @answer="answer"
      @decline="decline"
    />
    <KaptionCallDock
      v-if="showDock"
      :call="activeCall"
      :held-call="heldCall"
      :muted="calls.state.muted"
      :on-hold="calls.state.onHold"
      :minimised="calls.state.minimised"
      :client-state="calls.state.clientState"
      :media-error="calls.state.mediaError"
      :waiting-count="waitingCount"
      :mic-label="calls.state.micLabel"
      :recording="!!callRecording?.enabled"
      :recording-stopped="!!callRecording?.stoppedAt"
      :notice-mode="callRecording?.noticeMode || 'auto'"
      :notice-played-at="callRecording?.noticePlayedAt || null"
      :notice-busy="noticeBusy"
      :can-stop-recording="canStopRecording"
      :can-pass="canPass"
      :pass-targets="passTargets"
      :pass-targets-loading="passTargetsLoading"
      :pass-busy="passBusy"
      :outgoing-pass="calls.outgoingPass.value"
      :passed-notice="calls.passedNotice.value"
      :received-pass="receivedPass"
      :muted-after-pass="mutedAfterPass"
      @toggle-mute="calls.toggleMute"
      @toggle-hold="calls.toggleHold"
      @open-pass="loadPassTargets"
      @pass="startPass"
      @complete-pass="completePass"
      @take-call="takeCall"
      @cancel-pass="cancelPass"
      @undo-pass="calls.undoPass()"
      @leave-pass="leavePass"
      @notes="openConversation()"
      @end="calls.end()"
      @retry-media="calls.retryMedia()"
      @play-notice="playNotice"
      @stop-recording="calls.stopRecording()"
      @minimise="calls.setMinimised(true)"
      @expand="calls.setMinimised(false)"
      @open-conversation="openConversation()"
    />
    <!-- whatsapp-calling-ownership.UI.2 -->
    <KaptionPassLineDialog :current-user-id="currentUser?.id || null" />
  </div>
</template>
